export interface SelectedItemRef {
  id: number;
  name: string;
}

export interface PageSelectionState {
  allPageSelected: boolean;
  somePageSelected: boolean;
}

export function formatSelectedCount(
  count: number,
  singular: string,
  plural: string,
): string {
  return count === 1 ? `1 ${singular} selected` : `${count} ${plural} selected`;
}

export function personDisplayName(
  id: number,
  firstName?: string | null,
  lastName?: string | null,
  kind = "Person",
): string {
  const name = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return name || `${kind} #${id}`;
}

export function mergeUniqueIds(
  current: number[],
  incoming: number[],
): number[] {
  const seen = new Set(current);
  const next = [...current];

  for (const id of incoming) {
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    next.push(id);
  }

  return next;
}

export function removeIds(current: number[], idsToRemove: number[]): number[] {
  if (idsToRemove.length === 0) return current;
  const remove = new Set(idsToRemove);
  return current.filter((id) => !remove.has(id));
}

export function getPageSelectionState(
  selectedIds: number[],
  pageIds: number[],
): PageSelectionState {
  if (pageIds.length === 0) {
    return { allPageSelected: false, somePageSelected: false };
  }

  const selected = new Set(selectedIds);
  let selectedOnPage = 0;
  for (const id of pageIds) {
    if (selected.has(id)) selectedOnPage += 1;
  }

  return {
    allPageSelected: selectedOnPage === pageIds.length,
    somePageSelected: selectedOnPage > 0 && selectedOnPage < pageIds.length,
  };
}

export function togglePageIds(
  selectedIds: number[],
  pageIds: number[],
): number[] {
  const { allPageSelected } = getPageSelectionState(selectedIds, pageIds);
  return allPageSelected
    ? removeIds(selectedIds, pageIds)
    : mergeUniqueIds(selectedIds, pageIds);
}

export function rememberLabels(
  current: Record<number, string>,
  incoming: SelectedItemRef[],
  selectedIds: number[],
  fallback: (id: number) => string,
): Record<number, string> {
  const selected = new Set(selectedIds);
  const fromIncoming = new Map<number, string>();

  for (const item of incoming) {
    if (!selected.has(item.id)) continue;
    fromIncoming.set(item.id, item.name);
  }

  const next: Record<number, string> = {};
  for (const id of selectedIds) {
    next[id] = fromIncoming.get(id) ?? current[id] ?? fallback(id);
  }

  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);
  if (currentKeys.length !== nextKeys.length) return next;

  for (const id of selectedIds) {
    if (current[id] !== next[id]) return next;
  }

  return current;
}

export function mergeLabels(
  current: Record<number, string>,
  incoming: SelectedItemRef[],
): Record<number, string> {
  if (incoming.length === 0) return current;

  let changed = false;
  const next = { ...current };
  for (const item of incoming) {
    if (next[item.id] === item.name) continue;
    next[item.id] = item.name;
    changed = true;
  }

  return changed ? next : current;
}

export function getSelectedItems(
  selectedIds: number[],
  names: Record<number, string>,
  fallback: (id: number) => string,
): SelectedItemRef[] {
  return selectedIds.map((id) => ({
    id,
    name: names[id] ?? fallback(id),
  }));
}
